// ¿Esta cuenta puede usar el Agente? — ADR-2026-09-14-el-agente-es-de-pro-y-ultra.
//
// El Agente es Pro/Ultra. Sus puertas visibles ya lo respetan cada una por su lado (el
// ítem de la sidebar, la card del sheet de Generar en mobile, el flotante de
// Configuración), pero ninguna de las tres cierra la puerta de atrás: hasta este cambio
// `/agente` no la gateaba NADIE y alcanzaba con escribir la URL. Este hook es el cierre,
// y lo consumen los DOS lugares que dibujan el chat —la pantalla dedicada y el panel
// lateral—, que son por donde pasa cualquier camino de entrada, incluidos los botones
// "Preguntále a Atlas" en flujo.
//
// Vive acá y no en `planCapabilities` porque es la parte con React: la regla pura es
// `decidirAccesoAlAgente`, esto es sólo de dónde salen el plan y el flag.

import { useEffect, useState } from 'react';
import { decidirAccesoAlAgente, type AccesoAlAgente } from '../../lib/planCapabilities';
import { getUserPlan } from '../../lib/planUtils';
import { useUserGroup } from '../../hooks/useUserGroup';

export type { AccesoAlAgente };

/** Cuánto se espera a las consultas antes de decidir con lo que haya. Sin plazo, una que
 *  no vuelve nunca deja la pantalla en "cargando" para siempre — que no es dar acceso,
 *  pero tampoco es una pantalla. */
const ESPERA_MAX_MS = 5_000;

/** Si la consulta del plan vuelve vacía se reintenta UNA vez antes de denegar: separa el
 *  hipo de red (expulsaría a un Pro) de la cuenta que de verdad no tiene plan. */
const REINTENTO_MS = 600;

const esperar = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * En qué estado está el acceso de esta cuenta al Agente: `cargando`, `permitido` o
 * `denegado` (la regla, con tests, es `decidirAccesoAlAgente`).
 *
 * Tres estados y no un booleano porque el guard es lo ÚNICO que separa a una cuenta sin
 * plan del Agente, y las dos respuestas que necesita llegan por red:
 *
 *  • **El plan**, de `company_profiles` y no de la semilla de `localStorage`. La semilla
 *    sirve para que no parpadee una puerta; para CERRARLA no, porque la escribe el mismo
 *    navegador que se quiere gatear. Mientras la consulta no vuelve se espera; si vuelve
 *    vacía (falló, o la cuenta no tiene plan) se reintenta una vez y después se deniega —
 *    antes, con el plan en `null` se dejaba pasar, así que una consulta que fallaba
 *    dejaba a cualquiera adentro.
 *  • **`dev_tester`**, que pasa siempre. No es una excepción de favor: las pantallas
 *    internas del Estudio de marketing tienen botones "Preguntále a Atlas" en flujo y
 *    varias cuentas del equipo no están en un plan pago. Es el mismo trato que ya les da
 *    `modosVisibles` con los modos internos.
 */
export function useAccesoAlAgente(): AccesoAlAgente {
  const { isDevTester } = useUserGroup();
  /** `undefined` = la consulta todavía no contestó. `null` = contestó y no hay plan. */
  const [plan, setPlan] = useState<string | null | undefined>(undefined);
  const [plazoVencido, setPlazoVencido] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      const primera = await getUserPlan();
      if (cancelado) return;
      if (primera) {
        setPlan(primera);
        return;
      }
      await esperar(REINTENTO_MS);
      if (cancelado) return;
      const segunda = await getUserPlan();
      if (!cancelado) setPlan(segunda ?? null);
    })();
    const plazo = setTimeout(() => setPlazoVencido(true), ESPERA_MAX_MS);
    return () => {
      cancelado = true;
      clearTimeout(plazo);
    };
  }, []);

  return decidirAccesoAlAgente({ plan, isDevTester, plazoVencido });
}
